import * as React from "react";

import { cn } from "@/utils";

import { AnimatePresence, motion } from "motion/react";
import { PixelEffect } from "./pixel-effect-dynamic";
import { useState } from "react";

function Card({
  wrapperClassName,
  className,
  animateOnHover = false,
  backgroundVariant = "default",
  background,
  onClick,
  role,
  tabIndex,
  onKeyDown,
  "aria-label": ariaLabel,
  "aria-labelledby": ariaLabelledBy,
  "aria-describedby": ariaDescribedBy,
  ...props
}: React.ComponentProps<"div"> & {
  wrapperClassName?: string;
  animateOnHover?: boolean;
  transparent?: boolean;
  backgroundVariant?: "default" | "light" | "transparent";
  background?: React.ReactNode;
  onClick?: () => void;
}) {
  const [hovered, setHovered] = useState(false);
  return (
    // @ts-ignore
    <motion.div
      data-slot="card"
      className={cn(
        "h-full relative overflow-hidden bg-card text-card-foreground flex flex-col gap-6 rounded-none border py-4 md:py-6 shadow-sm",
        backgroundVariant === "light" && "bg-background-light",
        backgroundVariant === "transparent" && "bg-background/30",
        wrapperClassName && wrapperClassName
        // className
      )}
      onMouseEnter={() => setHovered(true)}
      onMouseLeave={() => setHovered(false)}
      onClick={onClick}
      role={role}
      tabIndex={tabIndex}
      onKeyDown={onKeyDown}
      aria-label={ariaLabel}
      aria-labelledby={ariaLabelledBy}
      aria-describedby={ariaDescribedBy}
    >
      {background}
      <div className={cn("relative z-20", className)} {...props}>
        {props.children}
      </div>
      <AnimatePresence>
        {hovered && animateOnHover && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            transition={{ duration: 0.05 }}
            className="h-full w-full absolute inset-0 card-hover-effect"
          >
            <>
              {/* <div className="bg-background absolute w-2/3 h-2/3 overflow-hidden top-0 left-0 rounded-br-[70%]"> */}
              <div className="bg-background absolute w-full h-full overflow-hidden top-0 left-0 rounded-br-[70%] z-10">
                <PixelEffect
                  containerClassName="bg-transparent"
                  animationSpeed={5}
                  colors={[[78, 64, 255]]}
                  opacities={[
                    0.25, 0.35, 0.45, 0.55, 0.65, 0.75, 0.85, 0.95, 1,
                  ]}
                  dotSize={3}
                  showGradient={false}
                />
                <div className="absolute top-0 left-0 w-full h-full bg-radial-[at_0%_0%] from-background/50 via-background via-70% to-background to-100%" />
              </div>
              <div className="z-10 bg-background absolute w-[70%] h-[70%] overflow-hidden bottom-0 right-0 rounded-tl-[100%] shadow-[inset_0_0_0_20px_black]">
                <PixelEffect
                  animationSpeed={5}
                  containerClassName="bg-transparent m-20"
                  colors={[[10, 255, 199]]}
                  opacities={[
                    0.25, 0.35, 0.45, 0.55, 0.65, 0.75, 0.85, 0.95, 1,
                  ]}
                  dotSize={3}
                  showGradient={false}
                />
                <div className="absolute top-0 left-0 w-full h-full bg-radial-[at_80%_100%] from-background/60 via-background to-black/90" />
              </div>
            </>
          </motion.div>
        )}
      </AnimatePresence>
    </motion.div>
  );
}

function CardHeader({ className, ...props }: React.ComponentProps<"div">) {
  return (
    <div
      data-slot="card-header"
      className={cn(
        "@container/card-header grid auto-rows-min grid-rows-[auto_auto] items-start gap-4 px-6 has-data-[slot=card-action]:grid-cols-[1fr_auto] [.border-b]:pb-6",
        className
      )}
      {...props}
    />
  );
}

function CardDate({ className, ...props }: React.ComponentProps<"div">) {
  return (
    <div
      data-slot="card-date"
      className={cn("text-primary-200 font-mono text-sm", className)}
      {...props}
    />
  );
}

function CardTitle({
  className,
  size = "sm",
  as = "div",
  clamp = true,
  ...props
}: {
  size?: "sm" | "md" | "lg";
  as?: "div" | "h3";
  clamp?: boolean;
} & React.HTMLAttributes<HTMLElement>) {
  const Comp = as === "h3" ? "h3" : "div";
  return (
    <Comp
      data-slot="card-title"
      className={cn(className, clamp && "line-clamp-3", {
        h6: size === "sm",
        h5: size === "md",
        "text-xl md:text-[26px]": size === "lg",
      })}
      {...props}
    />
  );
}

function CardDescription({
  className,
  size = "sm",
  clamp = true,
  ...props
}: React.ComponentProps<"div"> & {
  size?: "sm" | "md" | "lg";
  clamp?: boolean;
}) {
  return (
    <div
      data-slot="card-description"
      className={cn("text-muted-foreground !leading-[1.8]", className, {
        "p-sm line-clamp-2": clamp && size === "sm",
        "h6 line-clamp-3": clamp && size === "md",
        "h6 line-clamp-4": clamp && size === "lg",
      })}
      {...props}
    />
  );
}

function CardAction({ className, ...props }: React.ComponentProps<"div">) {
  return (
    <div
      data-slot="card-action"
      className={cn(
        "col-start-2 row-span-2 row-start-1 self-start justify-self-end",
        className
      )}
      {...props}
    />
  );
}

function CardContent({ className, ...props }: React.ComponentProps<"div">) {
  return (
    <div
      data-slot="card-content"
      className={cn("px-4 md:px-6", className)}
      {...props}
    />
  );
}

function CardFooter({ className, ...props }: React.ComponentProps<"div">) {
  return (
    <div
      data-slot="card-footer"
      className={cn("flex items-center px-6 [.border-t]:pt-6", className)}
      {...props}
    />
  );
}

export {
  Card,
  CardHeader,
  CardFooter,
  CardTitle,
  CardDate,
  CardAction,
  CardDescription,
  CardContent,
};
